/**
 * Luxury Top-Down Predictive Search Engine (431-88 Style)
 */
(function () {
  const DEBOUNCE_MS = 250;
  const RESULTS_ID = 'predictive-search-results';

  class SearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.cachedMap = new Map();
      this._handleEscapeKey = (e) => {
        if (e.code === 'Escape' && this.hasAttribute('active')) {
          this.close();
        }
      };
      this._handleCloseClick = (e) => {
        if (
          e.target.closest('[is="close-drawer"]') ||
          e.target.closest('#search-drawer-close-btn') ||
          e.target.closest('#SearchDrawer-Overlay')
        ) {
          e.preventDefault();
          this.close();
        }
      };

      this._debouncedFetch = this.debounce(() => this.executeSearch(), DEBOUNCE_MS);
    }

    debounce(fn, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    connectedCallback() {
      // Move to body to prevent stacking context or sticky header issues
      if (this.parentElement && this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      this._container = this.querySelector('.search-drawer-container');
      this._form = this.querySelector('form');
      this._input = this.querySelector('input[type="search"]');
      this._clearButton = this.querySelector('#search-clear-btn');
      this._resultsWrapper = this.querySelector('#search-results-wrapper');
      this._resultsContent = this.querySelector(`#${RESULTS_ID}`);

      document.addEventListener('keyup', this._handleEscapeKey);
      this.addEventListener('click', this._handleCloseClick);

      this.initFormAndInput();
      this.bindTriggers();
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this._handleEscapeKey);
      this.removeEventListener('click', this._handleCloseClick);
    }

    initFormAndInput() {
      if (this._input) {
        this._input.addEventListener('input', () => {
          const val = this._input.value.trim();
          if (val.length > 0) {
            if (this._clearButton) this._clearButton.classList.remove('hidden');
            this._debouncedFetch();
          } else {
            this.resetSearch();
          }
        });
      }

      if (this._clearButton) {
        this._clearButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.resetSearch();
        });
      }
    }

    bindTriggers() {
      const triggers = document.querySelectorAll(
        '[aria-controls="SearchDrawer"], #headerIconSearch, .header__search-link a, [href*="/search"]:not(.predictive-view-all-btn):not(.predictive-query-link)'
      );
      triggers.forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(trigger);
        });
      });
    }

    open(trigger) {
      this.previousFocused = trigger || document.activeElement;

      // Close mobile navigation sidebar if open
      const mobileSidebar = document.querySelector('header-sidebar');
      if (mobileSidebar) {
        if (typeof mobileSidebar.hide === 'function') mobileSidebar.hide();
        else if (typeof mobileSidebar.close === 'function') mobileSidebar.close();
      }

      // Pause Lenis smooth-scroll
      if (window.lenisInstance && typeof window.lenisInstance.stop === 'function') {
        window.lenisInstance.stop();
      }

      document.body.classList.add('sky-search-open');
      this.setAttribute('open', '');
      this.setAttribute('active', '');

      setTimeout(() => {
        if (this._input) {
          this._input.focus({ preventScroll: true });
        }
      }, 150);
    }

    close() {
      this.removeAttribute('active');
      if (this._container) this._container.classList.remove('is-full');

      setTimeout(() => {
        this.removeAttribute('open');
        document.body.classList.remove('sky-search-open');

        // Resume Lenis smooth scroll
        if (window.lenisInstance && typeof window.lenisInstance.start === 'function') {
          window.lenisInstance.start();
        }

        if (this.previousFocused && typeof this.previousFocused.focus === 'function') {
          this.previousFocused.focus({ preventScroll: true });
        }
      }, 250);
    }

    readQuery() {
      return this._input ? this._input.value.trim() : '';
    }

    executeSearch() {
      const query = this.readQuery();
      if (!query) {
        this.resetSearch();
        return;
      }

      const rootUrl = window.Shopify?.routes?.root || '/';
      const url = `${rootUrl}search/suggest?q=${encodeURIComponent(
        query
      )}&resources[type]=query,product,article&resources[limit]=8&resources[limit_scope]=each&section_id=predictive-search`;

      if (this.cachedMap.has(url)) {
        this.applyResultsHtml(this.cachedMap.get(url));
        return;
      }

      this.fetchResults(url);
    }

    fetchResults(url) {
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Search request failed');
          return response.text();
        })
        .then((html) => {
          this.applyResultsHtml(html);
          this.cachedMap.set(url, html);
        })
        .catch((err) => {
          console.error('Predictive Search error:', err);
        });
    }

    applyResultsHtml(responseText) {
      if (!this._resultsContent) {
        this._resultsContent = this.querySelector(`#${RESULTS_ID}`);
      }
      if (!this._resultsWrapper) {
        this._resultsWrapper = this.querySelector('#search-results-wrapper');
      }
      if (!this._container) {
        this._container = this.querySelector('.search-drawer-container');
      }
      if (!this._resultsContent || !this._resultsWrapper) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(responseText, 'text/html');
      const content = doc.getElementById(RESULTS_ID) || doc.body;

      this._resultsContent.innerHTML = content.innerHTML;
      this._resultsWrapper.classList.remove('hidden');
      if (this._container) {
        this._container.classList.add('is-full');
      }
    }

    resetSearch() {
      if (this._input) this._input.value = '';
      if (this._clearButton) this._clearButton.classList.add('hidden');
      if (this._resultsWrapper) this._resultsWrapper.classList.add('hidden');
      if (this._resultsContent) this._resultsContent.innerHTML = '';
      if (this._container) this._container.classList.remove('is-full');
      if (this._input) this._input.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('search-drawer')) {
    customElements.define('search-drawer', SearchDrawer);
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('search-drawer');
    if (drawer) {
      drawer.bindTriggers();
    }
  });
})();
